<script setup lang="ts">
import { type ComputedRef, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
// biome-ignore lint/correctness/noUnusedImports: used via tag <KioskLayout> in <template>
import KioskLayout from '@/app/layouts/kiosk/KioskLayout.vue';
import { store } from '@/app/store';
import { formatSum } from '@/services/currency/format-sum';
import { fromMinorUnits } from '@/services/currency/minor-units';
import { payment } from '@/services/Payment';
import { printing } from '@/services/Printing';
import { session } from '@/services/Session';
import { sentryService } from '@/services/telemetry/sentry';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
// biome-ignore lint/correctness/noUnusedImports: used via tag <KioskTerminalMessage> in <template>
import KioskTerminalMessage from './terminal/KioskTerminalMessage.vue';
import {
  FINAL_SCREEN_AUTO_END_MS,
  useSessionEndScreen,
} from './terminal/useSessionEndScreen';

interface PrintFailedPublicApi {
  _endSessionAndReturn: () => Promise<void>;
  readonly isNavigatingAway: boolean;
  onStartAgainClick: () => void;
}

const { t } = useI18n();
const sessionId: string | null = session.id as string | null;
// The screen tells only what occurred to the money: the amount that came back
// for refunded copies, the amount that the backend did not take for failed
// copies of a held payment, and a staff member when a paid copy did not print
// and has neither result. A copy is paid only when it carries its payment
// intent, thus an order without a card payment gets the plain caption.
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const caption: ComputedRef<string> = computed(() => {
  const refunds = printing.copyRefundSummary();
  if (refunds.needsStaff) {
    return t('PrintFailed.captionRefundHelp');
  }
  const currency = payment.currency || store.kiosk?.currency || 'eur';
  const amount = (cents: number): string =>
    formatSum(fromMinorUnits(cents, currency), currency);
  const sentences: string[] = [];
  if (refunds.refundedCents > 0) {
    sentences.push(
      t('PrintFailed.captionRefunded', {
        amount: amount(refunds.refundedCents),
      })
    );
  }
  if (refunds.notChargedCents > 0) {
    sentences.push(
      t('PrintFailed.captionNotCharged', {
        amount: amount(refunds.notChargedCents),
      })
    );
  }
  return sentences.length > 0 ? sentences.join(' ') : t('PrintFailed.caption');
});

// When some copies printed, the title gives the number of printed copies.
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const title: ComputedRef<string> = computed(() => {
  const { printed, total } = printing.printedCopyCount();
  return printed > 0 && printed < total
    ? t('PrintFailed.titlePartial', { printed, total })
    : t('PrintFailed.title');
});

onMounted(() => {
  exposeToGlobalScope(publicApi, 'PrintFailed');

  sentryService.addBreadcrumb('printing', 'Print-failed terminal shown', {
    session_id: sessionId,
    print_queue_count: store.printingTotalCount || 0,
  });
  sentryService.incrementCounter('printing.exited_via_print_failed', 1);
});

const { endSessionAndReturn, isNavigatingAway } = useSessionEndScreen({
  autoEndAfterMs: FINAL_SCREEN_AUTO_END_MS,
  screenName: 'PrintFailed',
});

function onStartAgainClick(): void {
  void endSessionAndReturn();
}

const publicApi: PrintFailedPublicApi = {
  _endSessionAndReturn: endSessionAndReturn,
  get isNavigatingAway(): boolean {
    return isNavigatingAway.value;
  },
  onStartAgainClick,
};

defineExpose(publicApi);
</script>

<template>
  <KioskLayout :is-centered="true">
    <KioskTerminalMessage
      action-test-id="print-failed-start-again"
      :action-disabled="isNavigatingAway"
      :action-label="t('Success.buttonStartAgain')"
      :caption="caption"
      caption-class="PrintFailed__caption"
      caption-max-width="34rem"
      caption-test-id="print-failed-caption"
      root-class="PrintFailed"
      root-test-id="print-failed-root"
      :title="title"
      @action="onStartAgainClick"
    />
  </KioskLayout>
</template>
